/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Paper,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import * as React from "react";

import { useAuth } from "@/providers";
import { RobotoTypography } from "@/shared/components";
import { MeteringReport, MeteredResourceValue } from "@/shared/domain/metering";
import { Resource } from "@/types/resources";

dayjs.extend(duration);
dayjs.extend(relativeTime);

function humanFileSize(size: number) {
  const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
  return (
    (size / Math.pow(1024, i)).toFixed(2) +
    " " +
    ["B", "kB", "MB", "GB", "TB"][i]
  );
}

function humanDuration(millis: number) {
  return (millis / 60000).toFixed(2) + " minutes";
}

export interface SystemUsageProps {
  latestReport: MeteringReport | null;
}

export const SystemUsage: React.FC<SystemUsageProps> = ({ latestReport }) => {
  const { currentUser } = useAuth();
  const theme = useTheme();

  const valuesByName: { [key: string]: MeteredResourceValue } = {};

  const lastUpdated = React.useMemo(() => {
    if (!latestReport?.end_time) {
      return "Never";
    }

    return dayjs
      .duration(dayjs(latestReport.end_time).diff(dayjs()))
      .humanize(true);
  }, [latestReport]);

  if (latestReport) {
    latestReport.values.forEach((value) => {
      valuesByName[value.resource_name] = value;
    });
  }

  const datasets = valuesByName[Resource.DatasetsCreated]?.quantity || 0;
  const invocations =
    valuesByName[Resource.InvocationsScheduled]?.quantity || 0;
  const invocationVCpuMillis =
    valuesByName[Resource.InvocationVCpuMillis]?.quantity || 0;
  const invocationRamMillis =
    valuesByName[Resource.InvocationRamMillis]?.quantity || 0;
  const invocationRuntimeMillis =
    valuesByName[Resource.InvocationRuntimeMillis]?.quantity || 0;
  const externalDownloadBytes =
    valuesByName[Resource.ManagedDataDownloadBytes]?.quantity || 0;
  const externalUploadBytes =
    valuesByName[Resource.ManagedDataUploadBytes]?.quantity || 0;
  const internalDownloadBytes =
    valuesByName[Resource.ManagedDataInternalDownloadBytes]?.quantity || 0;
  const internalUploadBytes =
    valuesByName[Resource.ManagedDataInternalUploadBytes]?.quantity || 0;
  const storedFiles =
    valuesByName[Resource.ManagedDataStoredFiles]?.quantity || 0;
  const storedFileBytes =
    valuesByName[Resource.ManagedDataStoredBytes]?.quantity || 0;

  let formattedInvocationVCpuTime =
    humanDuration(invocationVCpuMillis) + " * vCPUs";
  let formattedInvocationRamTime =
    humanDuration(invocationRamMillis) + " * GBs RAM";
  let formattedExternalDownloadBytes = humanFileSize(externalDownloadBytes);
  let formattedInternalDownloadBytes = humanFileSize(internalDownloadBytes);
  let formattedExternalUploadBytes = humanFileSize(externalUploadBytes);
  let formattedInternalUploadBytes = humanFileSize(internalUploadBytes);
  let formattedTotalStorage = `${storedFiles} Files, ${humanFileSize(
    storedFileBytes,
  )}`;

  const invocationVCpuCost =
    (0.04048 * invocationVCpuMillis) / (1000 * 60 * 60);
  const invocationRamCost = (0.004445 * invocationRamMillis) / (1000 * 60 * 60);
  const s3DownloadCost = (0.09 * externalDownloadBytes) / (1024 * 1024 * 1024);
  const s3StorageCost = (0.023 * storedFileBytes) / (1024 * 1024 * 1024);
  const totalCost =
    invocationVCpuCost + s3DownloadCost + s3StorageCost + invocationRamCost;

  if (currentUser?.is_system_user) {
    formattedInvocationVCpuTime += " ($" + `${invocationVCpuCost.toFixed(6)})`;
    formattedInvocationRamTime += " ($" + `${invocationRamCost.toFixed(6)})`;
    formattedExternalDownloadBytes += " ($" + `${s3DownloadCost.toFixed(6)})`;
    formattedInternalDownloadBytes += " (free)";
    formattedExternalUploadBytes += " (free)";
    formattedInternalUploadBytes += " (free)";
    formattedTotalStorage += " ($" + `${s3StorageCost.toFixed(6)})`;
  }

  const formattedInvocationStats = (
    <div>
      Scheduled: {invocations}
      <br />
      Total Runtime: {humanDuration(invocationRuntimeMillis)}
      <br />
      vCPU Usage: {formattedInvocationVCpuTime}
      <br />
      RAM Usage: {formattedInvocationRamTime}
      <br />
    </div>
  );

  const formattedDownloadBytes = (
    <div>
      Public Internet: {formattedExternalDownloadBytes}
      <br />
      Roboto Platform: {formattedInternalDownloadBytes}
    </div>
  );
  const formattedUploadBytes = (
    <div>
      Public Internet: {formattedExternalUploadBytes}
      <br />
      Roboto Platform: {formattedInternalUploadBytes}
    </div>
  );

  return (
    <>
      {currentUser?.is_system_user && (
        <Box
          component={Paper}
          variant="outlined"
          sx={{
            mt: theme.spacing(2),
            padding: theme.spacing(2),
            maxWidth: "400px",
            backgroundColor: theme.palette.foreground.main,
          }}
        >
          <RobotoTypography variant="subtitle2">
            Current Month-To-Date Usage
            {currentUser?.is_system_user
              ? " ($" + totalCost.toFixed(6) + ")"
              : ""}
          </RobotoTypography>
          <RobotoTypography variant="caption">
            Last updated {lastUpdated}
          </RobotoTypography>
          <List>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: "body2" }}
                primary={"Datasets"}
                secondary={"Created: " + datasets}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: "body2" }}
                primary={"Invocations"}
                secondary={formattedInvocationStats}
                secondaryTypographyProps={{
                  component: "span",
                }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: "body2" }}
                primary={"Downloads"}
                secondary={formattedDownloadBytes}
                secondaryTypographyProps={{
                  component: "span",
                }}
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: "body2" }}
                primary={"Uploads"}
                secondary={formattedUploadBytes}
                secondaryTypographyProps={{
                  component: "span",
                }}
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: "body2" }}
                primary={"Storage"}
                secondary={formattedTotalStorage}
                secondaryTypographyProps={{
                  component: "span",
                }}
              />
            </ListItem>
          </List>
        </Box>
      )}
    </>
  );
};
